import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
} from '@angular/core';
import { RelativeTimePipe } from '@context/frontend/common';
import { getUaDevice } from '@context/frontend/native/platform';
import { Content } from '@context/shared/types/common';
import { hasInheritedRolePermission, User } from '@context/shared/types/user';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiHint, TuiIcon, TuiSizeL, TuiSizeS } from '@taiga-ui/core';
import { TuiBadge } from '@taiga-ui/kit';
import { ContentThumbnailComponent } from '../content-thumbnail/content-thumbnail.component';

@Component({
	imports: [
		ContentThumbnailComponent,
		RelativeTimePipe,
		TranslocoModule,
		TuiIcon,
		TuiBadge,
		TuiHint,
	],
	selector: 'ctx-content-row',
	templateUrl: 'content-row.component.html',
	styleUrl: 'content-row.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[attr.as-table-row]': 'asTableRow()',
		'[attr.size]': 'size()',
		'[attr.device]': 'device',
	},
})
export class ContentRowComponent {
	readonly device = getUaDevice();

	readonly content = input.required<Content>();

	readonly size = input<TuiSizeL | TuiSizeS>('l');

	readonly interactable = input(true);

	readonly label = input<string | null>(null);

	readonly user = input<User | null>(null);

	readonly showAccess = input(true);

	readonly asTableRow = input(true);

	readonly role = computed(() => {
		const user = this.user();
		if (!user) return 'user';
		return user.role;
	});

	/**
	 * `true` if the content has been shared with the user
	 */
	readonly isShared = computed(() => {
		const user = this.user();
		const content = this.content();
		if (!user) return true;
		if (content.createdBy?.id === user.id) return false;
		return content.shared?.some((u) => u.id === user?.id);
	});

	/**
	 * `true` if the currently authenticated user has created this
	 */
	readonly isMine = computed(() => {
		const user = this.user();
		if (!user) return false;
		return this.content().createdBy?.id === user.id;
	});

	readonly roleAccess = computed(() => {
		const user = this.user();
		if (!user) return false;
		return hasInheritedRolePermission(user.role, 'admin');
	});
}
